import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Upload,
  BarChart3,
  Users,
  FileText,
  Settings,
  StickyNote,
  Trello,
  Loader,
  Calendar
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { navigationAPI } from '../../utils/api';
import { getErrorMessage } from '../../utils/errorTranslation';

const iconMap = {
  Briefcase,
  Upload,
  BarChart3,
  Users,
  FileText,
  Settings,
  StickyNote,
  Trello,
  Calendar
};

const colorMap = {
  blue: {
    icon: 'text-blue-600',
    hover: 'hover:border-blue-300 hover:bg-blue-50',
    badge: 'bg-blue-100 text-blue-600'
  },
  green: {
    icon: 'text-green-600',
    hover: 'hover:border-green-300 hover:bg-green-50',
    badge: 'bg-green-100 text-green-600'
  },
  purple: {
    icon: 'text-purple-600',
    hover: 'hover:border-purple-300 hover:bg-purple-50',
    badge: 'bg-purple-100 text-purple-600'
  },
  orange: {
    icon: 'text-orange-600',
    hover: 'hover:border-orange-300 hover:bg-orange-50',
    badge: 'bg-orange-100 text-orange-600'
  },
  emerald: {
    icon: 'text-emerald-600',
    hover: 'hover:border-emerald-300 hover:bg-emerald-50',
    badge: 'bg-emerald-100 text-emerald-600'
  },
  amber: {
    icon: 'text-amber-600',
    hover: 'hover:border-amber-300 hover:bg-amber-50',
    badge: 'bg-amber-100 text-amber-600'
  },
  gray: {
    icon: 'text-gray-600',
    hover: 'hover:border-gray-300 hover:bg-gray-50',
    badge: 'bg-gray-100 text-gray-600'
  },
  indigo: {
    icon: 'text-indigo-600',
    hover: 'hover:border-indigo-300 hover:bg-indigo-50',
    badge: 'bg-indigo-100 text-indigo-600'
  },
  teal: {
    icon: 'text-teal-600',
    hover: 'hover:border-teal-300 hover:bg-teal-50',
    badge: 'bg-teal-100 text-teal-600'
  }
};

const QuickActionsWidget = () => {
  const { t } = useLanguage();
  const [quickActions, setQuickActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const isNavigationTrackingEnabled =
    process.env.NODE_ENV === 'production' ||
    process.env.REACT_APP_ENABLE_NAV_TRACKING === 'true';

  useEffect(() => {
    loadQuickActions();
  }, []);

  const loadQuickActions = async () => {
    try {
      setLoading(true);
      const response = await navigationAPI.getQuickActions();
      const data = response.data;
      setQuickActions(data.quick_actions || []);
    } catch (error) {
      console.error('Error loading quick actions:', error);
      toast.error(getErrorMessage(error, t, t('dashboard.errors.quickActionsLoadFailed', 'Error loading quick actions')));
    } finally {
      setLoading(false);
    }
  };

  const trackNavigation = async (route, metadata = {}) => {
    if (!isNavigationTrackingEnabled) return;
    try {
      await navigationAPI.track({ route, metadata });
    } catch (error) {
      // Non-blocking analytics
    }
  };

  const formatFrequency = (count) => {
    if (count <= 1) return t('dashboard.widgets.frequency.once');
    if (count >= 10) return t('dashboard.widgets.frequency.many');
    return t('dashboard.widgets.frequency.times', { count });
  };

  const handleQuickAction = async (action) => {
    try {
      await trackNavigation(action.route, {
        action_id: action.id,
        action_title: action.title,
        triggered_from: 'quick_actions_widget'
      });
      navigate(action.route);
    } catch (error) {
      console.error('Error handling quick action:', error);
      toast.error(t('dashboard.widgets.navigationError'));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card h-full rounded-lg border border-gray-200 shadow-sm"
    >
      <div className="card-header">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {t('dashboard.widgets.quickActions')}
          </h3>
          <span className="text-xs text-gray-500">{t('dashboard.widgets.basedOnUsage')}</span>
        </div>
      </div>

      <div className="card-content">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="h-6 w-6 text-gray-400 animate-spin" />
          </div>
        ) : quickActions.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-6">
            {t('dashboard.widgets.noActionsAvailable')}
          </div>
        ) : (
          <div className="space-y-3">
            {quickActions.map((action, index) => {
              const IconComponent = iconMap[action.icon] || Briefcase;
              const colors = colorMap[action.color] || colorMap.blue;
              const frequency = action.stats?.frequency || 0;

              return (
                <motion.button
                  key={action.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleQuickAction(action)}
                  className={`w-full text-left p-3 rounded-lg border border-gray-200 ${colors.hover} transition-colors group`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <IconComponent className={`h-5 w-5 ${colors.icon} mr-3`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {action.title}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {action.description}
                        </p>
                      </div>
                    </div>

                    {frequency > 0 && (
                      <div className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${colors.badge}`}>
                        {formatFrequency(frequency)}
                      </div>
                    )}

                    {action.score > 4 && (
                      <div className="ml-1 w-2 h-2 bg-green-400 rounded-full" />
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default QuickActionsWidget;
